import { useState, useEffect } from "react";
import { authFetch } from "../api";

export default function ListingComments({ listingId }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  useEffect(() => {
    if (listingId) loadComments();
  }, [listingId]);

  const loadComments = async () => {
    try {
      const data = await authFetch(`/listings/${listingId}/comments`);
      setComments(data.comments || []);
    } catch (e) {
      console.error("Failed to load comments:", e);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || submitting) return;
    setSubmitting(true);
    try {
      await authFetch(`/listings/${listingId}/comments`, {
        method: "POST",
        body: JSON.stringify({ comment: newComment.trim() }),
      });
      setNewComment("");
      loadComments();
    } catch (e) {
      console.error("Failed to add comment:", e);
    }
    setSubmitting(false);
  };

  const handleEdit = async (commentId) => {
    if (!editText.trim()) return;
    try {
      await authFetch(`/comments/${commentId}`, {
        method: "PUT",
        body: JSON.stringify({ comment: editText.trim() }),
      });
      setEditingId(null);
      setEditText("");
      loadComments();
    } catch (e) {
      console.error("Failed to edit comment:", e);
    }
  };

  const handleDelete = async (commentId) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await authFetch(`/comments/${commentId}`, { method: "DELETE" });
      loadComments();
    } catch (e) {
      console.error("Failed to delete comment:", e);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString() + " " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getUserInitials = (user) => {
    if (!user) return "?";
    const f = (user.first_name || "")[0] || "";
    const l = (user.last_name || "")[0] || "";
    return (f + l).toUpperCase() || user.email?.[0]?.toUpperCase() || "?";
  };

  if (loading) return <div style={{ fontSize: "13px", color: "#999" }}>Loading comments...</div>;

  return (
    <div>
      {/* Add comment */}
      <form onSubmit={handleSubmit} style={{ display: "flex", gap: "8px", marginBottom: comments.length ? "16px" : 0 }}>
        <input
          type="text"
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Add a comment..."
          style={{
            flex: 1,
            padding: "8px 12px",
            border: "1px solid #ddd",
            borderRadius: "6px",
            fontSize: "13px",
            outline: "none",
          }}
          onFocus={(e) => e.target.style.borderColor = "#1976d2"}
          onBlur={(e) => e.target.style.borderColor = "#ddd"}
        />
        <button
          type="submit"
          disabled={!newComment.trim() || submitting}
          style={{
            padding: "8px 16px",
            backgroundColor: newComment.trim() ? "#1976d2" : "#ccc",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "13px",
            cursor: newComment.trim() ? "pointer" : "default",
          }}
        >
          {submitting ? "..." : "Post"}
        </button>
      </form>

      {/* Comments list */}
      {comments.map((c) => (
        <div key={c.id} style={{ display: "flex", gap: "10px", padding: "10px 0", borderBottom: "1px solid #f0f0f0" }}>
          {/* Avatar */}
          <div style={{
            width: 32, height: 32, borderRadius: "50%",
            backgroundColor: "#e3f2fd", color: "#1976d2",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "12px", fontWeight: "600", flexShrink: 0,
          }}>
            {c.user?.profile_picture_url
              ? <img src={c.user.profile_picture_url} alt="" style={{ width: 32, height: 32, borderRadius: "50%" }} />
              : getUserInitials(c.user)
            }
          </div>

          {/* Content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
              <span style={{ fontSize: "13px", fontWeight: "600", color: "#333" }}>
                {c.user?.first_name} {c.user?.last_name}
              </span>
              <span style={{ fontSize: "11px", color: "#999" }}>{formatDate(c.created_at)}</span>
              {c.updated_at !== c.created_at && (
                <span style={{ fontSize: "11px", color: "#bbb" }}>(edited)</span>
              )}
            </div>

            {editingId === c.id ? (
              <div style={{ display: "flex", gap: "6px" }}>
                <input
                  type="text"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleEdit(c.id)}
                  style={{ flex: 1, padding: "4px 8px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "13px" }}
                  autoFocus
                />
                <button onClick={() => handleEdit(c.id)} style={{ fontSize: "12px", color: "#1976d2", background: "none", border: "none", cursor: "pointer" }}>Save</button>
                <button onClick={() => setEditingId(null)} style={{ fontSize: "12px", color: "#999", background: "none", border: "none", cursor: "pointer" }}>Cancel</button>
              </div>
            ) : (
              <div style={{ fontSize: "13px", color: "#444", whiteSpace: "pre-wrap" }}>{c.comment}</div>
            )}

            {/* Actions */}
            {editingId !== c.id && (
              <div style={{ marginTop: "4px", display: "flex", gap: "12px" }}>
                <button
                  onClick={() => { setEditingId(c.id); setEditText(c.comment); }}
                  style={{ fontSize: "11px", color: "#999", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(c.id)}
                  style={{ fontSize: "11px", color: "#d32f2f", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                >
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      ))}

      {comments.length === 0 && (
        <div style={{ fontSize: "13px", color: "#bbb", textAlign: "center", padding: "8px 0" }}>
          No comments yet
        </div>
      )}
    </div>
  );
}
